import { Component } from '@theme/component';

/**
 * Hero banner carousel — window-reveal transition (`clip-path`, GPU-composited) that follows the
 * pointer on fine pointers; falls back to a cross-fade under `prefers-reduced-motion`.
 * Docs: docs/hero-banner-carousel.md
 *
 * @extends {Component<{
 *   viewport: HTMLElement,
 *   slidesContainer: HTMLElement,
 *   slides: HTMLElement[],
 *   zonePrev?: HTMLElement,
 *   zoneNext?: HTMLElement,
 *   cursor?: HTMLElement,
 *   cursorIcon?: HTMLElement,
 *   previous?: HTMLElement,
 *   next?: HTMLElement,
 *   progress?: HTMLElement,
 *   dots?: HTMLElement[],
 *   nav?: HTMLElement,
 *   navItems?: HTMLElement[],
 * }>}
 */
class HeroBannerCarousel extends Component {
  /** Side, in px, of the window that follows the pointer (the reference uses 50). */
  static PREVIEW_SIZE = 50;

  /** Minimum horizontal travel, in px, for a gesture to count as a swipe. */
  static SWIPE_THRESHOLD = 45;

  /** Accumulated horizontal wheel delta, in px, before a trackpad gesture counts as a navigation. */
  static WHEEL_THRESHOLD = 60;

  /** Quiet time, in ms, that ends a wheel gesture — trackpad momentum keeps firing long after the fingers lift. */
  static WHEEL_SETTLE = 400;

  requiredRefs = ['viewport', 'slidesContainer'];

  /** Slide currently previewing through the pointer window, if any. */
  #preview = null;

  /** Last known pointer position inside the viewport. */
  #lastPointer = null;

  /** True right after a swipe, to discard the synthetic click that follows it. */
  #swiped = false;

  /** Index of the slide currently on screen. */
  #index = 0;

  /** Autoplay interval id. */
  #timer = null;

  /** True while a reveal is running — guards against overlapping transitions. */
  #transitioning = false;

  /**
   * Destination asked for while a reveal was running, replayed when it finishes. Without it a
   * click landing inside the ~500ms transition is dropped and the control looks broken.
   * @type {{target: number, origin: {x: number, y: number}} | null}
   */
  #queued = null;

  /** Reasons autoplay is currently held (hover, off-screen, hidden tab, focus). */
  #holds = new Set();

  /** Aborts every listener this component registers. */
  #controller = new AbortController();

  /** Watches whether the carousel is on screen, so an off-screen hero doesn't autoplay. */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.slides.length === 0) return;

    const { signal } = this.#controller;

    this.#syncState();
    this.#playIntro();

    if (this.slides.length > 1) {
      this.#bindPointer(signal);
      this.#bindSwipe(signal);
      this.#bindWheel(signal);
      this.#bindNav(signal);
      this.#bindKeyboard(signal);
      this.#bindAutoplay(signal);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#observer?.disconnect();
    this.#stop();
  }

  /* ------------------------------------------------------------------ *
   * Public API — reached from Liquid via `on:click="/next"` etc.
   * ------------------------------------------------------------------ */

  /**
   * Advances to the next slide.
   * @param {PointerEvent | MouseEvent} [event]
   */
  next(event) {
    this.#go(this.#index + 1, event);
  }

  /**
   * Returns to the previous slide.
   * @param {PointerEvent | MouseEvent} [event]
   */
  previous(event) {
    this.#go(this.#index - 1, event);
  }

  /**
   * Jumps to a specific slide. Bound as `on:click="/select/{index}"`, so the index arrives first.
   * @param {number} index
   * @param {PointerEvent | MouseEvent} [event]
   */
  select(index, event) {
    this.#go(Number(index), event);
  }

  /** Slides currently rendered, in DOM order. */
  get slides() {
    const { slides } = this.refs;
    return Array.isArray(slides) ? slides : [];
  }

  /** Autoplay delay in ms, or `null` when autoplay is off. */
  get autoplayInterval() {
    const seconds = Number(this.getAttribute('autoplay'));
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : null;
  }

  /** Reveal duration in ms. */
  get transitionDuration() {
    const value = Number(this.getAttribute('transition-duration'));
    return Number.isFinite(value) && value > 0 ? value : 500;
  }

  /** Whether the custom cursor is allowed for this configuration. */
  get usesCursorReveal() {
    const mode = this.getAttribute('navigation-mode');
    return mode === 'cursor_reveal' || mode === 'both';
  }

  /**
   * How the cursor's box is drawn: `reveal` peeks at the next slide through it, `solid` fills it,
   * `arrow` drops it. Only `reveal` needs the preview clip.
   * @returns {'reveal' | 'solid' | 'arrow'}
   */
  get cursorStyle() {
    const value = this.getAttribute('cursor-style');
    return value === 'solid' || value === 'arrow' ? value : 'reveal';
  }

  /* ------------------------------------------------------------------ *
   * Navigation
   * ------------------------------------------------------------------ */

  /**
   * Resolves a target index (wrapping or clamping) and starts the transition.
   * @param {number} target
   * @param {PointerEvent | MouseEvent} [event]
   */
  #go(target, event) {
    const total = this.slides.length;
    if (total < 2) return;

    // Queue rather than drop: the reveal is ~500ms, long enough that a second click on the dots
    // lands inside it, and silently ignoring it reads as the control not working at all.
    if (this.#transitioning) {
      this.#queued = { target, origin: this.#originFrom(event) };
      return;
    }

    let index = target;

    if (this.hasAttribute('loop')) {
      index = ((target % total) + total) % total;
    } else {
      index = Math.min(Math.max(target, 0), total - 1);
    }

    if (index === this.#index) return;

    // A manual move restarts the autoplay countdown, so the chosen slide gets its full time.
    this.#restartIfPlaying();
    this.#transition(index, this.#originFrom(event));
  }

  /**
   * Viewport-relative point the reveal grows from. Falls back to the centre for keyboard,
   * autoplay and programmatic moves.
   * @param {PointerEvent | MouseEvent} [event]
   * @returns {{x: number, y: number}}
   */
  #originFrom(event) {
    const rect = this.refs.viewport.getBoundingClientRect();

    if (event && 'clientX' in event && (event.clientX || event.clientY)) {
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    }

    return this.#lastPointer ?? { x: rect.width / 2, y: rect.height / 2 };
  }

  /**
   * Builds the `clip-path` for a window of `size` centred on (x, y), matching the carousel radius.
   *
   * @param {number} x
   * @param {number} y
   * @param {number} size window side in px; 0 or less = fully open
   * @returns {string}
   */
  #windowClip(x, y, size) {
    const { width, height } = this.refs.viewport.getBoundingClientRect();
    const radius = this.#cornerRadius;

    if (size <= 0) return `inset(0px 0px 0px 0px round ${radius}px)`;

    const half = size / 2;
    const top = Math.max(0, y - half);
    const left = Math.max(0, x - half);
    const right = Math.max(0, width - (x + half));
    const bottom = Math.max(0, height - (y + half));

    return `inset(${top}px ${right}px ${bottom}px ${left}px round ${radius}px)`;
  }

  /** Current corner radius, so the reveal window's corners match the carousel's. */
  get #cornerRadius() {
    return parseFloat(getComputedStyle(this).borderTopLeftRadius) || 0;
  }

  /**
   * Runs the circular reveal from `origin`, then commits the new index.
   * @param {number} index
   * @param {{x: number, y: number}} origin
   */
  async #transition(index, origin) {
    const outgoing = this.slides[this.#index];
    const incoming = this.slides[index];
    if (!incoming) return;

    this.#transitioning = true;
    this.dataset.transitioning = '';

    // The incoming slide is usually the one that was previewing — the reveal continues from that clip.
    this.#clearPreview();

    this.#setSlideHidden(incoming, false);
    incoming.dataset.entering = '';

    const previousIndex = this.#index;
    this.#index = index;
    this.#syncControls();

    try {
      await this.#reveal(incoming, origin);
    } finally {
      // Runs even if the animation is cancelled (theme editor re-render, disconnect), so the
      // carousel can never be left with two visible slides.
      delete incoming.dataset.entering;
      incoming.style.removeProperty('clip-path');

      if (outgoing && outgoing !== incoming && this.#index !== previousIndex) {
        this.#setSlideHidden(outgoing, true);
      }

      this.#transitioning = false;
      delete this.dataset.transitioning;
    }

    // Replay the last destination asked for mid-reveal. Read and cleared before the call, so a
    // queued move that queues again cannot loop. Skipped once detached — a theme editor re-render
    // can remove the carousel mid-reveal, and `disconnectedCallback` has already torn everything
    // down by the time this runs.
    const queued = this.#queued;
    this.#queued = null;
    if (queued && this.isConnected) this.#resume(queued.target, queued.origin);
  }

  /**
   * Runs a queued destination. Same resolution as `#go` but with an origin already captured, since
   * the original event is long gone by the time the reveal ends.
   * @param {number} target
   * @param {{x: number, y: number}} origin
   */
  #resume(target, origin) {
    const total = this.slides.length;
    const index = this.hasAttribute('loop')
      ? ((target % total) + total) % total
      : Math.min(Math.max(target, 0), total - 1);

    if (index === this.#index) return;

    this.#restartIfPlaying();
    this.#transition(index, origin);
  }

  /**
   * The reveal itself: the little window the pointer was carrying grows until it covers the
   * carousel. Degrades to a cross-fade when motion is reduced or `clip-path` can't be animated.
   * @param {HTMLElement} incoming
   * @param {{x: number, y: number}} origin
   */
  async #reveal(incoming, origin) {
    const duration = this.transitionDuration;

    if (this.#prefersReducedMotion || !this.#supportsClipPath) {
      await this.#animate(incoming, [{ opacity: 0 }, { opacity: 1 }], duration);
      return;
    }

    const { x, y } = origin;

    await this.#animate(
      incoming,
      [
        { clipPath: this.#windowClip(x, y, HeroBannerCarousel.PREVIEW_SIZE) },
        { clipPath: this.#windowClip(x, y, 0) },
      ],
      duration
    );
  }

  /**
   * @param {HTMLElement} element
   * @param {Keyframe[]} keyframes
   * @param {number} duration
   */
  async #animate(element, keyframes, duration) {
    if (typeof element.animate !== 'function') return;

    const animation = element.animate(keyframes, {
      duration,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
      fill: 'none',
    });

    try {
      await animation.finished;
    } catch {
      // `finished` rejects when the animation is cancelled — not an error worth surfacing.
    }
  }

  get #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get #supportsClipPath() {
    return typeof CSS !== 'undefined' && CSS.supports?.('clip-path', 'circle(10px at 5px 5px)');
  }

  /* ------------------------------------------------------------------ *
   * State sync
   * ------------------------------------------------------------------ */

  /** Puts the DOM in a known state on connect (first slide visible, rest hidden). */
  #syncState() {
    for (const [index, slide] of this.slides.entries()) {
      this.#setSlideHidden(slide, index !== this.#index);
      delete slide.dataset.entering;
    }

    this.#syncControls();
  }

  /**
   * `aria-hidden` alone isn't enough — `inert` is required so an off-screen slide's CTA leaves the tab order.
   * @param {HTMLElement} slide
   * @param {boolean} hidden
   */
  #setSlideHidden(slide, hidden) {
    slide.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    slide.toggleAttribute('inert', hidden);
  }

  /** Keeps the progress tablist, the lateral list and the arrows in sync with the active slide. */
  #syncControls() {
    const { dots, navItems, previous, next } = this.refs;

    if (Array.isArray(dots)) {
      for (const [index, dot] of dots.entries()) {
        const active = index === this.#index;
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
        dot.setAttribute('tabindex', active ? '0' : '-1');
      }
    }

    /*
     * The lateral list (AS-1219015) reports the same state, and it reports it HERE rather than in
     * its own listener: `#syncControls` already runs on every index change, whatever caused it —
     * a dot, an arrow, a swipe, autoplay. Giving the list its own path would have meant a second
     * source of truth that agrees right up until one of those five moves it.
     *
     * A plain attribute, not aria: the labels are `aria-hidden` decoration (the tablist is the
     * announced control), so there is nothing here for a screen reader to be told about.
     */
    if (Array.isArray(navItems)) {
      for (const [index, item] of navItems.entries()) {
        item.toggleAttribute('data-active', index === this.#index);
      }
    }

    // Without looping, the arrows at either end lead nowhere — say so instead of failing silently.
    if (!this.hasAttribute('loop')) {
      const last = this.slides.length - 1;
      previous?.toggleAttribute('disabled', this.#index === 0);
      next?.toggleAttribute('disabled', this.#index === last);
    }
  }

  /* ------------------------------------------------------------------ *
   * Intro
   * ------------------------------------------------------------------ */

  /**
   * Sets `data-intro-done` on the SECOND animation frame — setting it synchronously would let the
   * browser collapse both states into one style recalculation and the zoom would never be seen.
   */
  #playIntro() {
    if (!this.hasAttribute('intro')) return;

    if (this.#prefersReducedMotion) {
      this.dataset.introDone = '';
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.dataset.introDone = '';
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Pointer: click zones + custom cursor
   * ------------------------------------------------------------------ */

  /** @param {AbortSignal} signal */
  #bindPointer(signal) {
    if (!this.usesCursorReveal) return;

    const { viewport, cursor } = this.refs;

    viewport.addEventListener(
      'pointermove',
      (event) => {
        // Coarse pointers (touch) get the real buttons instead — a window that follows a hovering
        // pointer is meaningless without one.
        if (event.pointerType !== 'mouse') return;
        if (this.#transitioning) return;

        this.dataset.cursorActive = '';
        cursor?.removeAttribute('hidden');

        const rect = viewport.getBoundingClientRect();
        const x = event.clientX - rect.left;
        const y = event.clientY - rect.top;
        this.#lastPointer = { x, y };

        this.style.setProperty('--cursor-x', `${x}px`);
        this.style.setProperty('--cursor-y', `${y}px`);

        // Which half the pointer is in decides both the direction and which slide peeks through.
        const direction = x < rect.width / 2 ? 'previous' : 'next';
        if (cursor && cursor.dataset.direction !== direction) {
          cursor.dataset.direction = direction;
        }

        this.#showPreview(direction, x, y);
      },
      { signal, passive: true }
    );

    viewport.addEventListener(
      'pointerleave',
      () => {
        delete this.dataset.cursorActive;
        cursor?.setAttribute('hidden', '');
        this.#clearPreview();
      },
      { signal }
    );

    // Discarded on resize/rotation so a keyboard/autoplay reveal falls back to centre, not a stale point.
    window.addEventListener(
      'resize',
      () => {
        this.#lastPointer = null;
        this.#clearPreview();
      },
      { signal, passive: true }
    );
  }

  /**
   * Resting state of the same `clip-path` the reveal animates, so a click continues from exactly here.
   *
   * @param {'next' | 'previous'} direction
   * @param {number} x
   * @param {number} y
   */
  #showPreview(direction, x, y) {
    const total = this.slides.length;
    if (total < 2) return;
    // Only the reveal style peeks at the next slide; the other two draw the cursor from CSS alone.
    if (this.cursorStyle !== 'reveal') return;
    if (this.#prefersReducedMotion || !this.#supportsClipPath) return;

    const offset = direction === 'next' ? 1 : -1;
    const target = this.hasAttribute('loop')
      ? (((this.#index + offset) % total) + total) % total
      : Math.min(Math.max(this.#index + offset, 0), total - 1);

    if (target === this.#index) return this.#clearPreview();

    const slide = this.slides[target];
    if (!slide) return;

    if (this.#preview && this.#preview !== slide) this.#clearPreview();

    this.#preview = slide;
    slide.dataset.preview = '';
    slide.style.clipPath = this.#windowClip(x, y, HeroBannerCarousel.PREVIEW_SIZE);
  }

  /** Puts the previewed slide back to sleep. */
  #clearPreview() {
    if (!this.#preview) return;

    delete this.#preview.dataset.preview;
    this.#preview.style.removeProperty('clip-path');
    this.#preview = null;
  }

  /* ------------------------------------------------------------------ *
   * Swipe (touch)
   * ------------------------------------------------------------------ */

  /**
   * Swipe navigation for touch — a gesture that travels more vertically than horizontally is left
   * to the browser as a scroll (`touch-action: pan-y` in the CSS).
   *
   * @param {AbortSignal} signal
   */
  /**
   * The lateral list drives the carousel through the SAME public `select` the dots use — it is a
   * second consumer of an API that already existed, not a second mechanism.
   *
   * `pointerenter` rather than `mouseenter` so a pen behaves like a mouse, and filtered to fine
   * pointers: a touch "hover" fires on tap and would make the list steal a tap meant for the link.
   * `focusin` gives the keyboard the same behaviour, so tabbing to a link previews the slide it
   * leads to instead of jumping somewhere unannounced.
   *
   * The autoplay hold is taken on the whole list, not per item, so moving between entries does not
   * release and re-take it — which would restart the countdown mid-read.
   *
   * @param {AbortSignal} signal
   */
  #bindNav(signal) {
    const { nav, navItems } = this.refs;
    if (!nav || !Array.isArray(navItems) || navItems.length === 0) return;

    for (const item of navItems) {
      const index = Number(item.dataset.index);
      if (!Number.isInteger(index)) continue;

      const activate = (/** @type {Event} */ event) => {
        if (event instanceof PointerEvent && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
        this.select(index, event);
      };

      item.addEventListener('pointerenter', activate, { signal });
      item.addEventListener('focusin', activate, { signal });
    }

    nav.addEventListener('pointerenter', () => this.#hold('nav'), { signal });
    nav.addEventListener('pointerleave', () => this.#release('nav'), { signal });
    nav.addEventListener('focusin', () => this.#hold('nav'), { signal });
    nav.addEventListener('focusout', () => this.#release('nav'), { signal });
  }

  /**
   * HORIZONTAL WHEEL — the trackpad gesture, which the swipe above can never see.
   *
   * A two-finger horizontal scroll is not a pointer drag: it arrives as `wheel` with `deltaX`, so
   * `#bindSwipe`'s Pointer Events never fire and the carousel looked dead to anyone on a laptop.
   * Reported from a trackpad, reproduced, and it is what the Loom asks for by name -- "si pasas
   * tambien el scroll entre las imagenes con el cursor o con el mouse, con el dedo pues igual".
   *
   * THE HARD PART IS MOMENTUM, NOT THE THRESHOLD. One flick emits dozens of events that keep coming
   * long after the fingers have lifted, so acting on each one walks the carousel to its last slide.
   * The gesture is therefore treated as a unit: deltas accumulate, ONE navigation fires when they
   * cross the threshold, and everything after it is swallowed until the wheel has been quiet for
   * `WHEEL_SETTLE`. Every further event pushes that quiet window out, so the tail of the momentum
   * extends the lock instead of starting a second gesture.
   *
   * Vertical intent is left alone and NOT prevented -- the visitor is scrolling the page past a
   * band that happens to sit under the pointer, and stealing that is the worst thing this could do.
   * `preventDefault` is called only on the horizontal axis we act on, which also stops Chrome's
   * two-finger back-navigation from firing on the same gesture.
   *
   * @param {AbortSignal} signal
   */
  #bindWheel(signal) {
    const { viewport } = this.refs;

    let accumulated = 0;
    let locked = false;
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let settle;

    const endGesture = () => {
      accumulated = 0;
      locked = false;
    };

    viewport.addEventListener(
      'wheel',
      (event) => {
        // Vertical intent: the page is scrolling, not the carousel. Leave it entirely alone.
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

        // Ours now, so the browser must not also treat it as a back gesture.
        event.preventDefault();

        clearTimeout(settle);
        settle = setTimeout(endGesture, HeroBannerCarousel.WHEEL_SETTLE);

        if (locked || this.#transitioning) return;

        accumulated += event.deltaX;

        if (Math.abs(accumulated) < HeroBannerCarousel.WHEEL_THRESHOLD) return;

        locked = true;
        if (accumulated > 0) this.next(event);
        else this.previous(event);
        accumulated = 0;
      },
      // Not passive: this listener has to be able to preventDefault the horizontal axis.
      { signal, passive: false }
    );
  }

  #bindSwipe(signal) {
    const { viewport } = this.refs;

    /** @type {{x: number, y: number, id: number} | null} */
    let start = null;

    const abandon = () => {
      if (!start) return;
      start = null;
      this.#release('swipe');
    };

    viewport.addEventListener(
      'pointerdown',
      (event) => {
        // Mouse already navigates via the click zones; only touch/pen matter here.
        if (event.pointerType === 'mouse' || this.#transitioning) return;

        this.#swiped = false;
        start = { x: event.clientX, y: event.clientY, id: event.pointerId };
        this.#hold('swipe');
      },
      { signal, passive: true }
    );

    viewport.addEventListener(
      'pointermove',
      (event) => {
        if (!start || event.pointerId !== start.id) return;

        // Vertical axis dominates: the visitor is scrolling, not navigating.
        if (Math.abs(event.clientY - start.y) > Math.abs(event.clientX - start.x)) abandon();
      },
      { signal, passive: true }
    );

    viewport.addEventListener(
      'pointerup',
      (event) => {
        if (!start || event.pointerId !== start.id) return;

        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        // Threshold relative to width, with an absolute minimum, so the gesture feels the same at any size.
        const threshold = Math.max(
          HeroBannerCarousel.SWIPE_THRESHOLD,
          viewport.clientWidth * 0.08
        );

        abandon();

        if (Math.abs(dx) < threshold || Math.abs(dx) <= Math.abs(dy)) return;

        // A swipe starting on the CTA shouldn't also follow the link.
        this.#swiped = true;

        if (dx < 0) this.next(event);
        else this.previous(event);
      },
      { signal }
    );

    viewport.addEventListener('pointercancel', abandon, { signal });

    // Cancels the synthetic click the browser fires after the gesture.
    viewport.addEventListener(
      'click',
      (event) => {
        if (!this.#swiped) return;

        event.preventDefault();
        event.stopPropagation();
        this.#swiped = false;
      },
      { signal, capture: true }
    );
  }

  /* ------------------------------------------------------------------ *
   * Keyboard
   * ------------------------------------------------------------------ */

  /** @param {AbortSignal} signal */
  #bindKeyboard(signal) {
    const { progress } = this.refs;
    if (!progress) return;

    // Arrow-key navigation inside the tablist, per the WAI-ARIA tabs pattern.
    progress.addEventListener(
      'keydown',
      (event) => {
        const keys = {
          ArrowLeft: this.#index - 1,
          ArrowRight: this.#index + 1,
          Home: 0,
          End: this.slides.length - 1,
        };

        const target = keys[event.key];
        if (target === undefined) return;

        event.preventDefault();
        this.#go(target);

        // Keep focus on the tab the visitor just moved to.
        const dots = this.refs.dots;
        if (Array.isArray(dots)) dots[this.#index]?.focus();
      },
      { signal }
    );
  }

  /* ------------------------------------------------------------------ *
   * Autoplay
   * ------------------------------------------------------------------ */

  /** @param {AbortSignal} signal */
  #bindAutoplay(signal) {
    if (this.autoplayInterval === null) return;

    if (this.hasAttribute('pause-on-hover')) {
      this.addEventListener('pointerenter', () => this.#hold('hover'), { signal });
      this.addEventListener('pointerleave', () => this.#release('hover'), { signal });
    }

    // Never steal a slide from under someone who is tabbing through the controls.
    this.addEventListener('focusin', () => this.#hold('focus'), { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.#release('focus');
    }, { signal });

    document.addEventListener(
      'visibilitychange',
      () => (document.hidden ? this.#hold('hidden') : this.#release('hidden')),
      { signal }
    );

    // An off-screen hero shouldn't burn frames or advance unseen.
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        ([entry]) => (entry?.isIntersecting ? this.#release('offscreen') : this.#hold('offscreen')),
        { threshold: 0.2 }
      );
      this.#observer.observe(this);
    }

    this.#play();
  }

  #play() {
    if (this.#timer !== null || this.#holds.size > 0) return;

    const interval = this.autoplayInterval;
    if (interval === null) return;

    this.#timer = window.setInterval(() => this.next(), interval);
    this.dataset.playing = '';
  }

  #stop() {
    if (this.#timer === null) return;

    window.clearInterval(this.#timer);
    this.#timer = null;
    delete this.dataset.playing;
  }

  /** Restarts the countdown after a manual move, so the new slide gets its full time. */
  #restartIfPlaying() {
    if (this.#timer === null) return;
    this.#stop();
    this.#play();
  }

  /** @param {string} reason */
  #hold(reason) {
    this.#holds.add(reason);
    this.#stop();
  }

  /** @param {string} reason */
  #release(reason) {
    this.#holds.delete(reason);
    if (this.#holds.size === 0) this.#play();
  }
}

if (!customElements.get('hero-banner-carousel')) {
  customElements.define('hero-banner-carousel', HeroBannerCarousel);
}
